import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { exportMonthRows } from "../lib/summary";
import { useTheme } from "../../../context/ThemeContext";
import { apiErrorMessage, fetchSummary, type LossesResponse, type SummaryResponse } from "../services/expensesService";
import { ErrorBox, Kpi, Notice, Page, Spinner } from "../components/ui";
import ClientScopeSelect from "../../work-hours/components/ClientScopeSelect";
import RosterPayrollCard from "../components/RosterPayrollCard";
import LossesCard from "../components/LossesCard";
import { budgetExecution } from "../lib/budget";
import { CATEGORY_COLORS, categoryIdList, categoryLabel, currentMonth, downloadText, money, monthLabel, shiftMonth } from "../lib/format";
import { applyMonthRange } from "../lib/monthRange";
import { ui } from "../theme";

// Resumen mes a mes (#613): cuánto se pagó cada mes, en qué, y contra el
// presupuesto. Incluye la nómina pagada y, donde no hay recibo, la nómina del
// equipo estimada desde el padrón. El cálculo de ejecución es el de @torre/costos.
// El periodo se elige con un rango «desde – hasta» de meses completos.

/** Serie de pérdidas en la gráfica mensual (#1177). */
const LOSS_LINE = "__losses";

/** Meses que abre el resumen: los últimos 12, con el mes en curso. */
const DEFAULT_MONTHS = 12;

export default function SummarySection() {
  const { t, i18n } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const [range, setRange] = useState(() => ({ from: shiftMonth(currentMonth(), -(DEFAULT_MONTHS - 1)), to: currentMonth() }));
  // Donde <input type="month"> se ve como texto (Firefox y Safari de escritorio), lo tecleado a
  // medias vive aquí y solo se aplica cuando ya es un mes válido; al salir del campo vuelve al rango.
  const [draft, setDraft] = useState(range);
  useEffect(() => setDraft(range), [range]);
  const [data, setData] = useState<SummaryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState(currentMonth());
  // #980: todos los clientes, uno, o solo lo propio (sin cliente).
  const [clientId, setClientId] = useState("");
  const [reload, setReload] = useState(0);
  // Pérdidas por mes (#1177): la tarjeta las carga y la gráfica las pinta como línea.
  const [losses, setLosses] = useState<LossesResponse | null>(null);
  // Las pérdidas son de la empresa: con un cliente elegido no se pintan (la tarjeta tampoco se monta).
  const showLosses = !clientId || clientId === "none";
  const lossByMonth = useMemo(
    () => (showLosses ? Object.fromEntries((losses?.months || []).map((m) => [m.period, m.total])) : {}),
    [losses, showLosses],
  );

  const changeRange = (change: { from?: string; to?: string }) => {
    setDraft((d) => ({ ...d, ...change }));
    const next = applyMonthRange(range, change);
    if (next.from === range.from && next.to === range.to) return;
    setRange(next);
    // El mes del detalle siempre es uno del rango: si queda fuera, pasa al último.
    setSelected((s) => (s < next.from || s > next.to ? next.to : s));
  };

  useEffect(() => {
    let alive = true;
    if (reload === 0) setData(null);
    fetchSummary({ from: range.from, to: range.to, clientId: clientId || undefined })
      .then((d) => {
        if (!alive) return;
        setData(d);
        setError(null);
      })
      .catch((err) => alive && setError(apiErrorMessage(err) || "load"));
    return () => {
      alive = false;
    };
  }, [range.from, range.to, clientId, reload]);

  const chart = useMemo(
    () =>
      (data?.months || []).map((m) => ({
        period: m.period,
        label: monthLabel(m.period, i18n.language),
        ...Object.fromEntries(categoryIdList().map((cat) => [cat, m.byCategory[cat] || 0])),
        [LOSS_LINE]: lossByMonth[m.period] || 0,
      })),
    [data, i18n.language, lossByMonth],
  );
  const hasLosses = showLosses && (losses?.total || 0) > 0;
  const usedCategories = categoryIdList().filter((cat) => (data?.months || []).some((m) => (m.byCategory[cat] || 0) > 0));

  const monthRow = data?.months.find((m) => m.period === selected);
  const prevRow = data?.months.find((m) => m.period === shiftMonth(selected, -1));
  const nonZero = (data?.months || []).filter((m) => m.total > 0);
  const average = nonZero.length ? nonZero.reduce((s, m) => s + m.total, 0) / nonZero.length : null;
  const execution = useMemo(
    () => (data ? budgetExecution(data.budgets, data.expenses, selected, i18n.language) : null),
    [data, selected, i18n.language],
  );
  const delta = monthRow && prevRow && prevRow.total > 0 ? ((monthRow.total - prevRow.total) / prevRow.total) * 100 : null;

  return (
    <Page
      title={t("expenses.menu.summary")}
      subtitle={t("expenses.summary.subtitle")}
      actions={
        <>
          <ClientScopeSelect value={clientId} onChange={setClientId} includeNone className={`${c.inputBase} w-auto`} />
          <div className="flex items-center gap-1.5" role="group" aria-label={t("expenses.summary.monthRange")}>
            <label className={`flex items-center gap-1.5 text-xs ${c.muted}`}>
              {t("expenses.summary.from")}
              <input
                type="month"
                aria-label={t("expenses.summary.from")}
                className={`${c.inputBase} w-auto`}
                placeholder={t("expenses.summary.monthPlaceholder")}
                value={draft.from}
                onChange={(e) => changeRange({ from: e.target.value })}
                onBlur={() => setDraft(range)}
              />
            </label>
            <label className={`flex items-center gap-1.5 text-xs ${c.muted}`}>
              {t("expenses.summary.to")}
              <input
                type="month"
                aria-label={t("expenses.summary.to")}
                className={`${c.inputBase} w-auto`}
                placeholder={t("expenses.summary.monthPlaceholder")}
                value={draft.to}
                onChange={(e) => changeRange({ to: e.target.value })}
                onBlur={() => setDraft(range)}
              />
            </label>
          </div>
          <button type="button" className={c.secondary} disabled={!data} onClick={() => data && downloadText("resumen-mensual.csv", exportMonthRows(data.months, i18n.language))}>
            <i className="ri-download-2-line mr-1" />
            CSV
          </button>
        </>
      }
    >
      <ErrorBox message={error === "load" ? t("expenses.errors.load") : error} />
      {!data && !error && <Spinner />}
      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi
              label={t("expenses.summary.thisMonth", { month: monthLabel(selected, i18n.language) })}
              value={money(monthRow?.total ?? 0, "MXN", i18n.language)}
              hint={delta == null ? undefined : t("expenses.summary.vsPrev", { pct: `${delta > 0 ? "+" : ""}${delta.toFixed(0)}%` })}
              tone={delta != null && delta > 15 ? "bad" : undefined}
            />
            <Kpi label={t("expenses.summary.rangeTotal", { count: data.months.length })} value={money(data.total, "MXN", i18n.language)} />
            <Kpi label={t("expenses.summary.average")} value={money(average, "MXN", i18n.language)} hint={t("expenses.summary.averageHint")} />
            <Kpi
              label={t("expenses.kpi.withoutInvoice")}
              value={String(data.months.reduce((s, m) => s + m.withoutInvoice, 0))}
              hint={t("expenses.summary.toReview", { count: data.months.reduce((s, m) => s + m.toReview, 0) })}
              tone={data.months.some((m) => m.withoutInvoice) ? "bad" : "good"}
            />
          </div>
          {data.months.some((m) => m.withoutRate > 0) && (
            <Notice tone="warn">{t("expenses.list.withoutRate", { count: data.months.reduce((s, m) => s + m.withoutRate, 0) })}</Notice>
          )}
          {data.payrollSkipped > 0 && <Notice tone="warn">{t("expenses.list.payrollSkipped", { count: data.payrollSkipped })}</Notice>}
          {/* La nómina del padrón es gasto propio: no aplica al filtrar un cliente. */}
          {data.roster && (!clientId || clientId === "none") && (
            <RosterPayrollCard
              key={JSON.stringify(data.roster.rates)}
              roster={data.roster}
              onSaved={() => setReload((n) => n + 1)}
            />
          )}

          {/* Pérdidas (#1177): son gasto propio, como la nómina; no aplican al filtrar un cliente. */}
          {showLosses && <LossesCard from={range.from} to={range.to} reload={reload} onData={setLosses} />}

          <div className={c.card}>
            <h3 className={`mb-3 text-sm font-semibold ${c.text}`}>{t("expenses.summary.byMonth")}</h3>
            {data.total === 0 ? (
              <p className={`py-10 text-center text-sm ${c.muted}`}>{t("expenses.summary.empty")}</p>
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chart} onClick={(e) => {
                    const p = (e as { activePayload?: { payload?: { period?: string } }[] })?.activePayload?.[0]?.payload?.period;
                    if (p) setSelected(p);
                  }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#333" : "#e5e7eb"} vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: isDarkMode ? "#9ca3af" : "#6b7280" }} />
                    <YAxis tick={{ fontSize: 11, fill: isDarkMode ? "#9ca3af" : "#6b7280" }} tickFormatter={(v) => `$${Math.round(Number(v) / 1000)}k`} width={48} />
                    <Tooltip
                      formatter={(v: number, name: string) => [money(v, "MXN", i18n.language), name === LOSS_LINE ? t("expenses.losses.title") : categoryLabel(name, i18n.language)]}
                      contentStyle={{ fontSize: 12 }}
                    />
                    <Legend formatter={(v: string) => (v === LOSS_LINE ? t("expenses.losses.title") : categoryLabel(v, i18n.language))} wrapperStyle={{ fontSize: 11 }} />
                    {usedCategories.map((cat) => (
                      <Bar key={cat} dataKey={cat} stackId="a" fill={CATEGORY_COLORS[cat]} cursor="pointer" />
                    ))}
                    {/* Pérdidas del mes (nómina sin justificar + otras): ya están dentro de las barras; la línea las destaca. */}
                    {hasLosses && <Line type="monotone" dataKey={LOSS_LINE} stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} />}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
            <p className={`mt-1 text-xs ${c.muted}`}>{t("expenses.summary.clickHint")}</p>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className={c.card}>
              <h3 className={`mb-2 text-sm font-semibold ${c.text}`}>{t("expenses.summary.monthTable")}</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className={`text-left text-xs ${c.muted}`}>
                    <th className="py-1">{t("expenses.summary.month")}</th>
                    <th className="py-1 text-right">{t("expenses.kpi.count")}</th>
                    <th className="py-1 text-right">{t("expenses.kpi.withoutInvoice")}</th>
                    <th className="py-1 text-right">{t("expenses.kpi.total")}</th>
                  </tr>
                </thead>
                <tbody>
                  {[...data.months].reverse().map((m) => (
                    <tr
                      key={m.period}
                      onClick={() => setSelected(m.period)}
                      className={`cursor-pointer border-t ${c.border} ${c.text} ${m.period === selected ? "bg-teal-500/10" : c.rowHover}`}
                    >
                      <td className="py-1.5">{monthLabel(m.period, i18n.language)}</td>
                      <td className="py-1.5 text-right tabular-nums">{m.count}</td>
                      <td className={`py-1.5 text-right tabular-nums ${m.withoutInvoice ? "text-amber-600" : ""}`}>{m.withoutInvoice}</td>
                      <td className="py-1.5 text-right font-semibold tabular-nums">{money(m.total, "MXN", i18n.language)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={c.card}>
              <h3 className={`mb-2 text-sm font-semibold ${c.text}`}>
                {t("expenses.summary.budgetFor", { month: monthLabel(selected, i18n.language) })}
              </h3>
              {execution && (
                <>
                  <p className={`mb-3 text-xs ${c.muted}`}>
                    {execution.porcentajeEjecucion == null
                      ? t("expenses.budgets.noBudget")
                      : t("expenses.budgets.executed", {
                          pct: execution.porcentajeEjecucion.toFixed(0),
                          spent: money(execution.totalGastado, "MXN", i18n.language),
                          budget: money(execution.totalPresupuestado, "MXN", i18n.language),
                        })}
                  </p>
                  <ul className="space-y-2">
                    {execution.porCentroCosto.map((row) => {
                      const pct = row.porcentajeEjecucion;
                      return (
                        <li key={row.centroCostoId}>
                          <div className={`flex justify-between text-xs ${c.text}`}>
                            <span className="flex items-center gap-1.5">
                              <span className="h-2 w-2 rounded-full" style={{ background: CATEGORY_COLORS[row.centroCostoId as keyof typeof CATEGORY_COLORS] || "#94a3b8" }} />
                              {row.centroCostoNombre}
                            </span>
                            <span className="tabular-nums">
                              {money(row.gastado, "MXN", i18n.language)}
                              <span className={c.muted}> / {row.presupuestado ? money(row.presupuestado, "MXN", i18n.language) : "—"}</span>
                            </span>
                          </div>
                          <div className={`mt-1 h-1.5 overflow-hidden rounded-full ${c.surface}`}>
                            <div
                              className={`h-full rounded-full ${row.sobregiro ? "bg-red-500" : "bg-teal-500"}`}
                              style={{ width: `${pct == null ? 100 : Math.min(100, pct)}%`, opacity: pct == null ? 0.35 : 1 }}
                            />
                          </div>
                        </li>
                      );
                    })}
                    {execution.porCentroCosto.length === 0 && <li className={`text-xs ${c.muted}`}>{t("expenses.summary.nothingThisMonth")}</li>}
                  </ul>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </Page>
  );
}
